import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandedCard, BrandedInput, BrandedButton, BootPulse } from '../components';
import { kratosService, KratosFlow } from '../services/kratosService';
import './Mfa.css';

const hasNode = (flow: KratosFlow | null, name: string): boolean =>
  !!flow?.ui?.nodes?.some((node) => node.attributes?.name === name);

/**
 * /mfa — second-factor challenge (login flow requesting aal2). Reached when
 * Kratos signals `session_aal2_required` from a login attempt. Renders
 * whichever second-factor methods the flow actually offers (TOTP and/or a
 * lookup/backup code) rather than assuming both are configured.
 */
export const Mfa: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') || '/settings';

  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [lookupCode, setLookupCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // aal=aal2 upgrades the current password session; no refresh needed.
        const initialFlow = await kratosService.initLoginFlow('aal2');
        setFlow(initialFlow);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to start the verification challenge.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const submitMethod = async (method: string, body: Record<string, unknown>) => {
    if (!flow) return;
    setError('');
    setSubmitting(true);

    try {
      await kratosService.submitLoginMethod(flow.id, { method, ...body });
      navigate(returnTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTotpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMethod('totp', { totp_code: totpCode });
  };

  const handleLookupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMethod('lookup_secret', { lookup_secret: lookupCode });
  };

  if (loading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading verification challenge" />
      </BrandedCard>
    );
  }

  const showTotp = hasNode(flow, 'totp_code');
  const showLookup = hasNode(flow, 'lookup_secret');

  return (
    <BrandedCard title="Verify It's You">
      {error && (
        <div className="mfa-error" role="alert">
          {error}
        </div>
      )}

      {showTotp && (
        <form onSubmit={handleTotpSubmit} className="mfa-form">
          <BrandedInput
            type="text"
            name="totp_code"
            label="Authenticator code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            placeholder="6-digit code"
            required
            autoComplete="one-time-code"
          />
          <BrandedButton type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Verifying...' : 'Verify code'}
          </BrandedButton>
        </form>
      )}

      {showTotp && showLookup && <div className="mfa-divider">or</div>}

      {showLookup && (
        <form onSubmit={handleLookupSubmit} className="mfa-form">
          <BrandedInput
            type="text"
            name="lookup_secret"
            label="Backup code"
            value={lookupCode}
            onChange={(e) => setLookupCode(e.target.value)}
            placeholder="Enter one of your saved backup codes"
            required
            autoComplete="one-time-code"
          />
          <BrandedButton type="submit" variant="secondary" fullWidth disabled={submitting}>
            {submitting ? 'Verifying...' : 'Use backup code'}
          </BrandedButton>
        </form>
      )}

      {!showTotp && !showLookup && !error && (
        <div className="mfa-info">
          No additional verification method is configured for this account.
        </div>
      )}

      <div className="mfa-links">
        <a href="/login" className="mfa-link">
          Back to sign in
        </a>
      </div>
    </BrandedCard>
  );
};
